// Copyright (c) Docugami, Inc. All rights reserved.

import { useCallback, useEffect, useState } from 'react';
import { INotificationListItem, NotificationsList } from "./NotificationsList";
import { useFetchNotifications } from "hooks/FetchNotifications";
import { useServiceNotificationMarkAsRead } from "hooks/ServiceNotificationMarkAsRead";
import { NotificationCategory } from "networking/NotificationsApi";
import { useServiceNotificationProvider } from 'hooks/ServiceNotificationProvider';
import { useClientNotificationProvider } from 'hooks/ClientNotificationProvider';
import { NotificationProviderType } from 'notifications/INotificationProvider';

export enum ReadSemantics {
    OnClose,
    OnOpen,
    OnAction
}

interface NotificationsViewProps {
    filter: NotificationCategory[];
    readSemantics: ReadSemantics;
    maxItems?: number;
    emptyView?: JSX.Element;
}

/**
 * Displays a list of notifications across the application. Supports filtering its view based on
 * notification category and changing the read semantics for notifications it displays.
 */
export const NotificationsView = (props: NotificationsViewProps): JSX.Element => {
    const [items, setItems] = useState<INotificationListItem[]>([]);

    useFetchNotifications(true);
    const serviceMarkAsRead = useServiceNotificationMarkAsRead();
    const { descriptors: serviceDescriptors, createComponent: createServiceNotification } = useServiceNotificationProvider();
    const { descriptors: clientDescriptors, createComponent: createClientNotification } = useClientNotificationProvider();

    const markAllAsRead = useCallback((force: boolean) => {
        serviceMarkAsRead(serviceDescriptors.map(item => item.id), force);
    }, [serviceDescriptors, serviceMarkAsRead]);

    useEffect(() => {
        return () => {
            if (props.readSemantics === ReadSemantics.OnClose) {
                markAllAsRead(true);
            }
        }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []);

    useEffect(() => {
        const allDescriptors = [...serviceDescriptors, ...clientDescriptors];
        let sortedDescriptors = allDescriptors
            .filter(n => props.filter.length === 0 || props.filter.includes(n.category))
            .sort((a, b) => {
                return b.timestamp.valueOf() - a.timestamp.valueOf()
            });

        if (!!props.maxItems) {
            sortedDescriptors = sortedDescriptors.slice(0, props.maxItems);
        }

        const newItems = sortedDescriptors.map(descriptor => {
            return {
                key: descriptor.id,
                value: descriptor.providerType === NotificationProviderType.Service ? createServiceNotification(descriptor) : createClientNotification(descriptor)
            };
        }).filter(x => !!x.value) as INotificationListItem[];

        setItems(newItems);
    }, [serviceDescriptors, props.filter, createServiceNotification, props.maxItems, clientDescriptors, createClientNotification]);

    useEffect(() => {
        if (props.readSemantics === ReadSemantics.OnOpen) {
            markAllAsRead(false);
        }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items, serviceMarkAsRead, props.readSemantics]);

    function getView(): JSX.Element {
        if (items.length === 0 && !!props.emptyView) {
            return props.emptyView;
        }

        return <NotificationsList notifications={items} />
    }

    return getView();
}
